<template>
  <v-container fluid>

    <!-- timeline datapoints msg ~*~*~*~*~*~* -->
    <div v-if="loaded && (dataPoints.length == 0)">
      <i class="mdi mdi-24px mdi-information-outline secondary--text mr-1"></i>
      <span class="body-2 font-italic font-weight-medium">
        Guidance currently recommends no actions.
      </span>    
    </div>

    <v-row align-start justify-center> <!-- wrap align-start justify-center> -->
      
      <!-- timeline ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
      <v-col v-if="loaded && (dataPoints.length > 0)" md="3" sm="12">
        <CoreTimeline
          v-model="dataPoints"
          :points="dataPoints"
          :calculated="meta.calculatedAt"
          no-data-msg="No Run Events"
          class="align-start"
        ></CoreTimeline>
      </v-col>

      <!-- chart & table ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
      <v-col v-if="hasAccess('GUIDANCE_REPORT')" :md="(loaded && dataPoints.length > 0) ? 9 : 12" sm="12">
            <!-- constraints, configuration popup ~*~*~*~*~*~*~*~*~ -->
            <GuidanceConfigPopout
              :json="rawData.facility.configuration"
              :key="`config-${componentKey}`"
              v-if="loaded && hasAccess('GUIDANCE_REPORT')"
            ></GuidanceConfigPopout>

            <!-- system log on dev  ~*~*~*~*~*~*~*~*~ -->
            <GuidanceLogPopout
              :content="rawData.log" 
              :key="`syslog-${componentKey}`"
              v-if="(loaded && rawData.log && hasAccess('GUIDANCE_RUN_LOG'))"
            ></GuidanceLogPopout>

            <!-- chart ~*~*~*~*~*~*~*~*~*~*~* -->
            <GuidanceChart
              v-if="loaded && hasAccess('GUIDANCE_REPORT')"
              :intervals="rawData.intervals"
              :calculated="meta.calculatedAt"
              :intervalLength="rawData.facility.intervalLengthSeconds"
              :rampUpMinutes="rampUpMinutes"
              :rampDownMinutes="rampDownMinutes"
              :key="`chart-${componentKey}`"
              class="up50"
            ></GuidanceChart>

            <!-- table ~*~*~*~*~*~*~*~*~*~*~* -->
            <GuidanceTable
              :intervals="rawData.intervals"
              :key="`table-${componentKey}`"
              v-if="loaded && hasAccess('GUIDANCE_REPORT')"
            ></GuidanceTable>
        </v-col>
    </v-row>

    <!-- data change alert ~*~*~*~*~*~*~*~*~*~*~*~*~*~* -->
    <audio id="audioAlarm" preload="auto">
      <source
        id="alarmAudioByte"
        type="audio/mp3"
        src="@/assets/audio/urgent.mp3"
      />
      <!-- Your browser does not support the audio tag. -->
    </audio>

  </v-container>
</template>

<script>
import FacilityService from "@/services/FacilityService";
import ConstraintService from "@/services/ConstraintService";
import moment from "moment";
import momentTZ from "moment-timezone";
import COLORS from "@/util/colors";
import { mapGetters } from "vuex";
export default {
    name: "Guidance",
    data() {
        return {
            currentTime: null,
            rawData: [],
            preparedConfigData: [],
            meta: {
                guidanceReportId: "",
                reportDate: "",
                calculatedAt: ""
            },
            timer: "",
            loaded: false,
            componentKey: 0,
            rampUpMinutes: null,
            rampDownMinutes: null,
            env: process.env.VUE_APP_ENV,
            dataPoints: []
        };
    },
    computed: {
        ...mapGetters([
            "getFacilityId",
            "getFacilityThreshold",
            "getFacilityTimezone",
            "getNcpCeiling",
            "hasAccess"
        ])
    },
    watch: {
        rawData: {
            handler: function() {
                this.parse();
                this.parseConfigForRamping();
                this.parsePoints();
            }
        }
    },
    mounted() {
        // initialize
        this.loaded = false;
        this.currentTime = this.$_datetimeMethod_currentTimeInZone(
            this.getFacilityTimezone,
            "YYYY-MM-DD HH:mm"
        );

        // fetch interval data
        this.fetch();

        // set time to refetch data every 15 minutes
        this.timer = setInterval(this.fetch, 1000 * 60 * 15);
    },

    beforeDestroy() {
        clearInterval(this.timer);
    },

    // == Methods
    methods: {
        fetch() {
            // console.log('fetch()')
            // fresh start
            this.loaded = false;

            // when rawData changes, watcher kicks off
            FacilityService.getLatestForecast(this.getFacilityId)
                .then(response => {
                    this.rawData = response.data;
                })
                .catch(error => {
                    this.$toast.error(
                        "Error loading forecast; " + error.message,
                        { timeout: false }
                    );
                });
        },

        parse() {
            // parse and store data
            this.meta.guidanceReportId = this.rawData.guidanceReportId;
            this.meta.reportDate = this.rawData.reportDate;
            this.meta.calculatedAt = this.$_datetimeMethod_formatTimestamp(
                this.rawData.calculatedAt,
                this.getFacilityTimezone,
                "YYYY-MM-DD HH:mm:ss"
            );

            // guidance reports changes?  alert user & sound alarm
            if (this.rawData.runsChangedSinceLastReport) {
                this.$toast.warning(
                    "Sustaine recommendations have changed. Please consult the Sustaine Console for details.",
                    { timeout: false }
                );
                this.soundAlert();
            }

            // forces component keys to update and component refreshes
            this.componentKey += 1;

            // elvis leaves the bulding...
            this.loaded = true;
        },

        parsePoints() {
            let rightThisMinute = momentTZ(moment()).tz(this.getFacilityTimezone);
            if (this.env != "production") {
                rightThisMinute = moment(this.meta.calculatedAt);
            }

            this.dataPoints = [];
            let inRunEvent = false;
            for (var i = 0; i < this.rawData.intervals.length; i++) {
                let interval = this.rawData.intervals[i];
                // FUTURE data ...
                let ending = moment(
                    momentTZ(interval.ending).tz(this.getFacilityTimezone)
                );

                // console.log(ending.format('MM/DD HH:mm') + " after " + rightThisMinute.format('MM/DD HH:mm') + " ?")
                if (ending.isAfter(rightThisMinute)) {
                    // console.log("guidance == MUST_RUN?", interval.guidance)
                    if (interval.guidance === "MUST_RUN") {
                        if (!inRunEvent) {
                            inRunEvent = true;

                            /* ramp up point */
                            let rampup_time = momentTZ(interval.ending)
                                .tz(this.getFacilityTimezone)
                                .subtract(this.rampUpMinutes, "minutes");
                            
                            // console.log("ending " + ending.format('MM/DD HH:mm') + "  rampup " + rampup_time.format('MM/DD HH:mm'))

                            if (rampup_time.isAfter(rightThisMinute)) {
                                let uppoint = {
                                    time: rampup_time.format("MM/DD HH:mm"),
                                    title: "RAMP UP",
                                    color: COLORS.hex.rampUp,
                                    description: ""
                                };
                                this.dataPoints.push(uppoint);

                                /* ramp up alert */
                                let diff = rampup_time.diff( rightThisMinute, "minutes" );
                                if (diff > 0 && diff < this.rampUpMinutes) {
                                    if (this.alerts) {
                                        this.$toast.info( "Ramping up in " + diff + " minutes.", { timeout: 5000 } );
                                    }
                                }
                            }
                        }

                        /* regular run points */
                        let description = "";
                        if (this.expanded) {
                            description = this.expandDescription(interval);
                        }

                        let point = {
                            time: ending.format("MM/DD HH:mm"),
                            title: "RUN @" + parseInt(interval.targetOutputKw) + " kW",
                            color: COLORS.hex.run,
                            description: description
                        };
                        this.dataPoints.push(point);
                    } else {
                        /*  if-else guidance=MUST_RUN */
                        // check for a ramp down
                        if (inRunEvent) {
                            inRunEvent = false;

                            let rampdown_time = momentTZ(interval.ending)
                                .tz(this.getFacilityTimezone)
                                .add(this.rampDownMinutes, "minutes")
                                .subtract(this.intervalLengthSeconds,"seconds");

                            let downpoint = {
                                time: rampdown_time.format("MM/DD  HH:mm"),
                                title: "RAMP DOWN",
                                color: COLORS.hex.rampDown,
                                description: ""
                            };
                            this.dataPoints.push(downpoint);

                            /* ramp down alert */
                            let diff = rampdown_time.diff(rightThisMinute,"minutes");
                            if (diff > 0 && diff < this.rampUpMinutes) {
                                if (this.alerts) {
                                    this.$toast.info(
                                        "Ramping down in " + diff + " minutes.",
                                        { timeout: 5000 }
                                    );
                                }
                            }
                        }
                    }
                }
            } /* foreach interval */

            // final timeline point is NOW
            if (this.dataPoints.length > 0) {
                this.dataPoints.splice(0, 0, {
                    time: rightThisMinute.format("MM/DD  HH:mm"),
                    title: "NOW",
                    color: COLORS.hex.time,
                    description: ""
                });
            }
            // console.log("dataPoints", this.dataPoints)
        },

        parseConfigForRamping() {
            this.rampUpMinutes = null;
            this.rampDownMinutes = null;

            let s1 = JSON.stringify(this.rawData.facility.configuration);
            let s2 = s1.replace(new RegExp("{", "g"), "");
            let s3 = s2.replace(new RegExp("}", "g"), "");
            let s4 = s3.replace(new RegExp('"', "g"), "");
            let s5 = s4.split(",");
            for (var c = 0; c < s5.length; c++) {
                let key = s5[c].split(":")[0];
                let val = s5[c].split(":")[1];
                if (key == "rampUpMinutes") {
                    this.rampUpMinutes = parseInt(val);
                }
                if (key == "rampDownMinutes") {
                    this.rampDownMinutes = parseInt(val);
                }
            }
            // if either config param is null, fetch via API
            if (this.rampUpMinutes == null || this.rampDownMinutes == null) {
                this.fetchConfigs();
            }
        },
        fetchConfigs() {
            ConstraintService.getFacilityConstraintByName(
                this.getFacilityId,
                encodeURI("Ramp Up")
            )
                .then(response => {
                    this.rampUpMinutes =
                        parseInt(response.data.value) > 0
                            ? parseInt(response.data.value)
                            : 60;
                })
                .catch(error => {});

            // Ramp Down
            ConstraintService.getFacilityConstraintByName(
                this.getFacilityId,
                encodeURI("Ramp Down")
            )
                .then(response => {
                    this.rampDownMinutes =
                        parseInt(response.data.value) > 0
                            ? parseInt(response.data.value)
                            : 30;
                })
                .catch(error => {});
        },
        soundAlert() {
            // audio
            let el = document.getElementById("audioAlarm");
            if (el) {
                el.play().catch(function() {
                    // console.log('audio alarm exception')
                });
            }
        },
        cancelAutoUpdate: function() {
            clearInterval(this.timer);
        }
    }
};
</script>
